<template>
  <v-card class="card-container synthetic-dashboard">
    <div class="main-toolbar mb-4">
      <div class="d-flex align-center justify-space-between" style="width: 100%">
        <div class="d-flex align-end">
          <div id="iconTitle">
            <v-icon id="icon" large color="#fff">mdi-view-dashboard-variant</v-icon>
          </div>
          <v-toolbar-title class="main-toolbar-title">
            {{ $t('Dashboard Sintético') }}
          </v-toolbar-title>
        </div>
        <SlideToUnlock
          v-if="canManageData"
          :locked="!unlocked"
          @unlock="unlocked = true"
        />
      </div>
    </div>

    <div class="main-toolbar mb-0 hidden-md-and-up">
      <v-tabs
        v-model="tab"
        :height="40"
        :mobile-break-point="300"
        icons-and-text
        class="ma-auto"
      >
        <v-tabs-slider color="#BBCF20"></v-tabs-slider>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-tab
              active-class="activated-tab"
              class="rounded-lg rounded-b-0"
              href="#tabQuantitative"
              v-on="on"
              v-bind="attrs"
            >
              <v-icon>mdi-signal</v-icon>
            </v-tab>
          </template>
          <span>{{ $t('Quantitativos') }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-tab
              active-class="activated-tab"
              class="rounded-lg rounded-b-0"
              href="#tabTimes"
              v-on="on"
              v-bind="attrs"
            >
              <v-icon>mdi-timer-sand-empty</v-icon>
            </v-tab>
          </template>
          <span>{{ $t('Tempos') }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-tab
              active-class="activated-tab"
              class="rounded-lg rounded-b-0"
              href="#tabCosts"
              v-on="on"
              v-bind="attrs"
            >
              <v-icon>mdi-currency-usd</v-icon>
            </v-tab>
          </template>
          <span>{{ $t('Custos') }}</span>
        </v-tooltip>
      </v-tabs>
    </div>

    <div class="main-toolbar mb-0 hidden-sm-and-down">
      <v-tabs
        v-model="tab"
        :height="40"
        :mobile-break-point="360"
        icons-and-text
        class="ma-0"
      >
        <v-tabs-slider color="#BBCF20"></v-tabs-slider>
        <v-tab
          active-class="activated-tab"
          class="rounded-lg rounded-b-0"
          href="#tabQuantitative"
        >
          {{ $t('Quantitativos') }}
          <v-icon class="mt-1 mr-2">mdi-signal</v-icon>
        </v-tab>
        <v-tab
          active-class="activated-tab"
          class="rounded-lg rounded-b-0"
          href="#tabTimes"
        >
          {{ $t('Tempos') }}
          <v-icon class="mt-1 mr-2">mdi-timer-sand-empty</v-icon>
        </v-tab>
        <v-tab
          active-class="activated-tab"
          class="rounded-lg rounded-b-0"
          href="#tabCosts"
        >
          {{ $t('Custos') }}
          <v-icon class="mt-1 mr-2">mdi-currency-usd</v-icon>
        </v-tab>
      </v-tabs>
    </div>

    <v-container v-if="canManageData" class="pa-0 ma-0 mt-2">
      <v-card
        max-width="800"
        color="#F8F8F8"
        class="pa-2 px-6 mx-auto report-card mb-4"
      >
        <v-card-text class="d-flex align-center mb-2">
          <v-icon class="mr-2">mdi-magnify</v-icon>
          <span class="subtitle">{{ $t('Filtros de Busca') }}</span>
        </v-card-text>

        <v-row class="ma-0" dense>
          <v-col cols="12" sm="6">
            <label class="label ml-1">{{ $t('Instituição') }}</label>
            <v-autocomplete
              hide-details="auto"
              v-model="institutionId"
              :items="institutions"
              item-text="name"
              item-value="id"
              :loading="institutionLoading"
              outlined
              disabled
              class="primary-input"
              :search-input.sync="institutionSearch"
              @blur="institutionSearch = ''"
            ></v-autocomplete>
          </v-col>

          <v-col cols="12" sm="6">
            <label class="label ml-1">{{ $t('Unidade') }}</label>
            <v-autocomplete
              hide-details="auto"
              v-model="unidadeIds"
              multiple
              :items="unidades"
              item-value="id"
              item-text="name"
              :loading="unidadeLoading"
              :placeholder="$t('Todas')"
              outlined
              class="primary-input"
              :search-input.sync="unitySearch"
              @blur="unitySearch = ''"
              clearable
              chips
              small-chips
            >
              <template v-slot:selection="{ attrs, index, item, select, selected }">
                <v-chip
                  v-bind="attrs"
                  :input-value="selected"
                  close
                  color="#BBCF20"
                  text-color="#fff"
                  small
                  @click="select"
                  @click:close="removeUnity(index)"
                >
                  {{ item.name }}
                </v-chip>
              </template>
              <template v-slot:item="{ item }">
                <span class="text-caption">{{ item.name }}</span>
              </template>
            </v-autocomplete>
          </v-col>

          <v-col cols="12" sm="4">
            <label class="label ml-1">{{ $t('Causa Defeito') }}</label>
            <v-autocomplete
              hide-details="auto"
              v-model="filter.defectCauses"
              :items="causeDefects"
              item-text="text"
              item-value="value"
              multiple
              autocomplete="off"
              outlined
              class="primary-input"
              :search-input.sync="defectCauseSearch"
              @blur="defectCauseSearch = ''"
              clearable
              chips
              small-chips
              :placeholder="$t('Todas')"
            >
              <template v-slot:selection="{ attrs, index, item, select, selected }">
                <v-chip
                  v-bind="attrs"
                  :input-value="selected"
                  close
                  color="#BBCF20"
                  text-color="#fff"
                  small
                  @click="select"
                  @click:close="removeCauseDefect(index)"
                >
                  {{ item.text }}
                </v-chip>
              </template>
              <template v-slot:item="{ item }">
                <span class="text-caption">{{ item.text }}</span>
              </template>
            </v-autocomplete>
          </v-col>

          <v-col cols="12" sm="4">
            <label class="label ml-1">{{ $t('Equipamento Padrão') }}</label>
            <v-autocomplete
              hide-details="auto"
              v-model="filter.standardEquipment"
              multiple
              :items="standardEquipments"
              item-text="name"
              return-object
              :placeholder="$t('Todos')"
              outlined
              class="primary-input"
              :search-input.sync="standardEquipmentSearch"
              @blur="standardEquipmentSearch = ''"
              clearable
              chips
              small-chips
            >
              <template v-slot:selection="{ attrs, index, item, select, selected }">
                <v-chip
                  v-bind="attrs"
                  :input-value="selected"
                  close
                  color="#BBCF20"
                  text-color="#fff"
                  small
                  @click="select"
                  @click:close="removeStandardEquipment(index)"
                >
                  {{ item.name }}
                </v-chip>
              </template>
              <template v-slot:item="{ item }">
                <span class="text-caption">{{ item.name }}</span>
              </template>
            </v-autocomplete>
          </v-col>

          <v-col cols="12" sm="4">
            <label class="label ml-1">{{ $t('Setor') }}</label>
            <v-autocomplete
              hide-details="auto"
              v-model="filter.departmentIds"
              multiple
              :items="localDepartments"
              item-value="id"
              item-text="name"
              :loading="departmentLoading"
              :placeholder="$t('Todas')"
              outlined
              class="primary-input"
              clearable
              chips
              small-chips
            >
              <template v-slot:selection="{ attrs, index, item, select, selected }">
                <v-chip
                  v-bind="attrs"
                  :input-value="selected"
                  close
                  color="#BBCF20"
                  text-color="#fff"
                  small
                  @click="select"
                  @click:close="removeDepartment(index)"
                >
                  {{ item.name }}
                </v-chip>
              </template>
              <template v-slot:item="{ item }">
                <span class="text-caption" v-if="item.unidade">
                  {{ item.name }} / {{ item.unidade.name }}
                </span>
                <span class="text-caption" v-else>{{ item.name }}</span>
              </template>
            </v-autocomplete>
          </v-col>

          <v-col cols="12" class="d-flex align-center justify-center mb-2 flex-wrap">
            <v-menu
              ref="startMenu"
              :close-on-content-click="false"
              :return-value.sync="startDate"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  depressed
                  class="primary-button"
                  style="text-transform: none"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                >
                  {{ formatMonth(startDate) }}
                </v-btn>
              </template>
              <v-date-picker
                v-model="startDate"
                type="month"
                :locale="$i18n.locale"
                no-title
                scrollable
                @change="$refs.startMenu.save(startDate)"
              ></v-date-picker>
            </v-menu>

            <span class="mx-2 subtitle">{{ $t('até') }}</span>

            <v-menu
              ref="endMenu"
              :close-on-content-click="false"
              :return-value.sync="endDate"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  depressed
                  class="primary-button"
                  style="text-transform: none"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                >
                  {{ formatMonth(endDate) }}
                </v-btn>
              </template>
              <v-date-picker
                v-model="endDate"
                type="month"
                :locale="$i18n.locale"
                no-title
                scrollable
                @change="$refs.endMenu.save(endDate)"
              ></v-date-picker>
            </v-menu>
          </v-col>

          <v-col class="d-flex align-center justify-center mb-4 flex-wrap" cols="12">
            <v-btn
              depressed
              class="secondary-button mr-1"
              :disabled="!unlocked"
              :loading="updatingData"
              @click="updateData"
            >
              <span>{{ $t('Atualizar dados') }}</span>
              <v-icon class="ml-2">mdi-database-refresh</v-icon>
            </v-btn>
            <v-btn
              v-if="snapshotInfo"
              depressed
              class="primary-button mr-1"
              :loading="pdfLoading"
              @click="exportPdf"
            >
              <span>{{ $t('Exportar PDF') }}</span>
              <v-icon class="ml-2">mdi-file-pdf-box</v-icon>
            </v-btn>
            <v-btn depressed class="clear-button ml-1" @click="reset">
              <span>{{ $t('Limpar') }}</span>
              <v-icon class="ml-2">mdi-cached</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-card>
    </v-container>

    <v-container v-if="snapshotInfo" class="pa-0 ma-0 mb-2">
      <div class="d-flex justify-center">
        <v-chip small color="#e7eaeb" class="text-caption">
          <v-icon small class="mr-1">mdi-clock-outline</v-icon>
          {{ $t('Última atualização') }}: {{ formatSnapshotDate(snapshotInfo.generatedAt) }}
        </v-chip>
      </div>
    </v-container>

    <v-container v-if="!snapshotReady" class="d-flex justify-center align-center py-12">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </v-container>

    <v-container v-else-if="!snapshotInfo" class="d-flex flex-column align-center py-12">
      <v-icon large color="#aaa">mdi-database-off-outline</v-icon>
      <p class="mt-4 text--secondary text-center">
        {{ $t('Nenhum dado disponível. Clique em "Atualizar dados" para gerar o relatório.') }}
      </p>
    </v-container>

    <v-tabs-items v-else v-model="tab">
      <v-tab-item eager value="tabQuantitative">
        <v-container>
          <v-row>
            <v-col cols="12">
              <CorrectiveScheXExec
                :filterProp="componentFilters['corrective_sched_x_exec'] || filter"
                :institution="institution"
                :unidades="unidadeIds"
                :isResult="true"
                :title="indicatorTitle('corrective_sched_x_exec')"
              />
            </v-col>
            <v-col cols="12">
              <ScheduledMaintenance
                :filterProp="componentFilters['scheduled_maintenance'] || filter"
                :institution="institution"
                :unidades="unidadeIds"
                :isResult="true"
                :title="indicatorTitle('scheduled_maintenance')"
              />
            </v-col>
            <v-col cols="12">
              <InappropriateUseEquipment
                :filterProp="componentFilters['user_cause_defect'] || filter"
                :institution="institution"
                :unidades="unidadeIds"
                :isResult="true"
                :title="indicatorTitle('user_cause_defect')"
              />
            </v-col>
            <v-col cols="12">
              <RecurringCalls
                :filterProp="componentFilters['recurring_calls'] || filter"
                :institution="institution"
                :unidades="unidadeIds"
                :isResult="true"
                :title="indicatorTitle('recurring_calls')"
              />
            </v-col>
          </v-row>
        </v-container>
      </v-tab-item>

      <v-tab-item eager value="tabTimes">
        <v-container>
          <v-row>
            <v-col cols="12">
              <StoppedMachine
                :filterProp="componentFilters['stopped_machine'] || filter"
                :institution="institution"
                :unidades="unidadeIds"
                :standardEquipments="standardEquipments"
                :isResult="true"
                :title="indicatorTitle('stopped_machine')"
              />
            </v-col>
            <v-col cols="12">
              <ServiceTimeCrit
                :filterProp="componentFilters['service_time_criticality'] || filter"
                :institution="institution"
                :unidades="unidadeIds"
                :standardEquipments="standardEquipments"
                :isResult="true"
                :title="indicatorTitle('service_time_criticality')"
              />
            </v-col>
            <v-col cols="12">
              <ServiceForecast
                :filterProp="componentFilters['service_forecast'] || filter"
                :institution="institution"
                :unidades="unidadeIds"
                :isResult="true"
                :title="indicatorTitle('service_forecast')"
              />
            </v-col>
            <v-col cols="12">
              <CallsAnsweredChart
                :filterProp="componentFilters['calls_answered'] || filter"
                :institution="institution"
                :unidades="unidadeIds"
                :isResult="true"
                :title="indicatorTitle('calls_answered')"
              />
            </v-col>
            <v-col cols="12">
              <TotalResolutionTimeCrit
                :filterProp="componentFilters['total_resolution_time_criticality'] || filter"
                :institution="institution"
                :unidades="unidadeIds"
                :isResult="true"
                :title="indicatorTitle('total_resolution_time_criticality')"
              />
            </v-col>
            <v-col cols="12">
              <ResolutionForecast
                :filterProp="componentFilters['resolution_forecast'] || filter"
                :institution="institution"
                :unidades="unidadeIds"
                :isResult="true"
                :title="indicatorTitle('resolution_forecast')"
              />
            </v-col>
            <v-col cols="12">
              <CallsResolvedChart
                :filterProp="componentFilters['calls_resolved'] || filter"
                :institution="institution"
                :unidades="unidadeIds"
                :isResult="true"
                :title="indicatorTitle('calls_resolved')"
              />
            </v-col>
          </v-row>
        </v-container>
      </v-tab-item>

      <v-tab-item eager value="tabCosts">
        <v-container>
          <v-row>
            <v-col cols="12">
              <CostPerSector
                :filterProp="componentFilters['cost_per_sector'] || filter"
                :institution="institution"
                :unidades="unidadeIds"
                :isResult="true"
                :title="indicatorTitle('cost_per_sector')"
              />
            </v-col>
            <v-col cols="12">
              <CostPerPiece
                :filterProp="componentFilters['cost_per_piece'] || filter"
                :institution="institution"
                :unidades="unidadeIds"
                :isResult="true"
                :title="indicatorTitle('cost_per_piece')"
              />
            </v-col>
            <v-col cols="12">
              <CostPerService
                :filterProp="componentFilters['cost_per_service'] || filter"
                :institution="institution"
                :unidades="unidadeIds"
                :isResult="true"
                :title="indicatorTitle('cost_per_service')"
              />
            </v-col>
            <v-col cols="12">
              <AvoidedCosts
                :filterProp="componentFilters['avoided_costs'] || filter"
                :institution="institution"
                :unidades="unidadeIds"
                :isResult="true"
                :title="indicatorTitle('avoided_costs')"
              />
            </v-col>
          </v-row>
        </v-container>
      </v-tab-item>
    </v-tabs-items>
  </v-card>
</template>

<script>
import SlideToUnlock from './SlideToUnlock.vue'
import ApiService from '../../../services/ApiService'
import GeneralService from '../../../services/GeneralService'
import moment from 'moment'
import {
  getIndicatorById,
  getCategoryById,
  getVisibleIndicators,
} from '../Reports/indicators/indicatorRegistry'

import CorrectiveScheXExec from '../Reports/components/GenerateReport/components/charts/quantitative/correctiveSchedXExec.vue'
import ScheduledMaintenance from '../Reports/components/GenerateReport/components/charts/quantitative/scheduledMaintenance.vue'
import InappropriateUseEquipment from '../Reports/components/GenerateReport/components/charts/quantitative/inappropriateUseEquipment.vue'
import RecurringCalls from '../Reports/components/GenerateReport/components/charts/quantitative/recurringCalls.vue'
import StoppedMachine from '../Reports/components/GenerateReport/components/charts/time/stoppedMachine.vue'
import ServiceTimeCrit from '../Reports/components/GenerateReport/components/charts/time/serviceTimeCrit.vue'
import ServiceForecast from '../Reports/components/GenerateReport/components/charts/control/serviceForecast.vue'
import CallsAnsweredChart from '../Reports/components/GenerateReport/components/charts/control/CallsAnsweredChart.vue'
import TotalResolutionTimeCrit from '../Reports/components/GenerateReport/components/charts/time/totalResolutionTimeCrit.vue'
import ResolutionForecast from '../Reports/components/GenerateReport/components/charts/control/resolutionForecast.vue'
import CallsResolvedChart from '../Reports/components/GenerateReport/components/charts/control/CallsResolvedChart.vue'
import CostPerSector from '../Reports/components/GenerateReport/components/charts/costs/costPerSector.vue'
import CostPerPiece from '../Reports/components/GenerateReport/components/charts/costs/costPerPiece.vue'
import CostPerService from '../Reports/components/GenerateReport/components/charts/costs/costPerService.vue'
import AvoidedCosts from '../Reports/components/GenerateReport/components/charts/costs/avoidedCosts.vue'

// inappropriateUseEquipment passes filterProp.data directly (no content wrapper)
const DIRECT_DATA_INDICATORS = new Set(['user_cause_defect'])

export default {
  name: 'SyntheticDashboard',

  components: {
    SlideToUnlock,
    CorrectiveScheXExec,
    ScheduledMaintenance,
    InappropriateUseEquipment,
    RecurringCalls,
    StoppedMachine,
    ServiceTimeCrit,
    ServiceForecast,
    CallsAnsweredChart,
    TotalResolutionTimeCrit,
    ResolutionForecast,
    CallsResolvedChart,
    CostPerSector,
    CostPerPiece,
    CostPerService,
    AvoidedCosts,
  },

  data: () => ({
    tab: null,
    institutionSearch: '',
    unitySearch: '',
    standardEquipmentSearch: '',
    defectCauseSearch: null,
    institution: {},
    institutionLoading: false,
    unidadeLoading: false,
    departmentLoading: false,
    localDepartments: [],
    causeDefects: [
      { text: 'Não aplicável', value: 0 },
      { text: 'Falha intrínseca', value: 1 },
      { text: 'Falha infraestrutura', value: 2 },
      { text: 'Erro operacional', value: 3 },
      { text: 'Dano acidental', value: 4 },
      { text: 'Abuso na utilização', value: 5 },
    ],
    menuStartDate: false,
    menuEndDate: false,
    startDate: null,
    endDate: null,
    page: 1,
    pageSize: 7,
    institutionId: null,
    institutions: [],
    unidadeIds: [],
    unidades: [],
    standardEquipments: [],
    userLogged: {},
    filter: {
      page: 1,
      pageSize: 7,
      institutionId: null,
      unidadeIds: [],
      startDate: null,
      endDate: null,
      standardEquipment: [],
      defectCauses: [],
      departmentIds: [],
    },
    snapshotReady: false,
    snapshotInfo: null,
    componentFilters: {},
    unlocked: false,
    updatingData: false,
    pdfLoading: false,
    apiService: new ApiService(),
    generalService: new GeneralService(),
  }),

  computed: {
    canManageData() {
      return (
        this.userLogged.mainType === 0 ||
        this.userLogged.mainType === 1 ||
        this.institution.userType === 8
      )
    },
  },

  async created() {
    this.userLogged = JSON.parse(localStorage.getItem('user'))
    this.institution = JSON.parse(localStorage.getItem('institution'))
    this.institutionId = this.institution.id
    this.filter.institutionId = this.institutionId

    this.getInstitutions()

    if (this.institution.userType > 1) {
      this.getUserUnidades()
    } else {
      this.getUnidades()
    }

    this.getStandardEquipments()
    this.getDepartments()

    this.startDate = moment()
      .add(-1, 'month')
      .clone()
      .startOf('month')
      .format('YYYY-MM-DD')

    this.endDate = moment()
      .add(-1, 'month')
      .clone()
      .endOf('month')
      .format('YYYY-MM-DD')

    this.setDates()

    await this.loadSnapshot()
  },

  methods: {
    indicatorTitle(id) {
      const ind = getIndicatorById(id)
      if (!ind) return ''
      const cat = getCategoryById(ind.categoryId)
      const visible = getVisibleIndicators(ind.categoryId, this.institution)
      const found = visible.find((v) => v.id === id)
      if (!found || !cat) return ind.nameKey
      return `${cat.prefix}.${found.displayNumber} - ${this.$t(ind.nameKey)}`
    },

    setDates() {
      this.filter.startDate = moment(this.startDate)
        .clone()
        .startOf('month')
        .format('YYYY-MM-DD')
      this.filter.endDate = moment(this.endDate)
        .clone()
        .endOf('month')
        .format('YYYY-MM-DD')
    },

    formatMonth(date) {
      return moment(date).locale(this.$i18n.locale).format('MMMM/YYYY')
    },

    formatSnapshotDate(date) {
      return moment(date).locale(this.$i18n.locale).format('DD/MM/YYYY HH:mm')
    },

    async loadSnapshot() {
      try {
        const response = await this.apiService.get(
          `syntheticdashboardsnapshot/${this.institutionId}`
        )
        if (response.content) {
          this.snapshotInfo = response.content

          if (response.content.filterJson) {
            this.restoreFilterFromSnapshot(JSON.parse(response.content.filterJson))
          }

          const snapshotMap = JSON.parse(response.content.snapshotJson)
          this.buildComponentFilters(snapshotMap)
        }
      } catch {
        // no snapshot available
      } finally {
        this.snapshotReady = true
      }
    },

    restoreFilterFromSnapshot(saved) {
      const get = (pascal, camel) => saved[pascal] ?? saved[camel]

      const startDate = get('StartDate', 'startDate')
      const endDate = get('EndDate', 'endDate')
      const unidadeIds = get('UnidadeIds', 'unidadeIds')
      const departmentIds = get('DepartmentIds', 'departmentIds')
      const defectCauses = get('DefectCauses', 'defectCauses')
      const standardEquipment = get('StandardEquipment', 'standardEquipment')

      if (startDate) this.startDate = moment(startDate).format('YYYY-MM-DD')
      if (endDate) this.endDate = moment(endDate).format('YYYY-MM-DD')
      if (Array.isArray(unidadeIds)) this.unidadeIds = unidadeIds
      if (Array.isArray(departmentIds)) this.filter.departmentIds = departmentIds
      if (Array.isArray(defectCauses)) this.filter.defectCauses = defectCauses
      if (Array.isArray(standardEquipment)) this.filter.standardEquipment = standardEquipment

      this.setDates()
    },

    buildComponentFilters(snapshotMap) {
      const filters = {}
      for (const [key, content] of Object.entries(snapshotMap)) {
        const dataStr = DIRECT_DATA_INDICATORS.has(key)
          ? JSON.stringify(content)
          : JSON.stringify({ content })
        filters[key] = { ...this.filter, data: dataStr }
      }
      this.componentFilters = filters
    },

    async updateData() {
      this.updatingData = true
      try {
        this.setDates()
        const filterPayload = {
          institutionId: this.institutionId,
          unidadeIds: this.unidadeIds,
          departmentIds: this.filter.departmentIds,
          defectCauses: this.filter.defectCauses,
          standardEquipment: this.filter.standardEquipment,
          startDate: this.filter.startDate,
          endDate: this.filter.endDate,
        }
        await this.apiService.post('syntheticdashboardsnapshot/generate', filterPayload)
        this.$toast.success(this.$t('Dados atualizados com sucesso'))
        this.snapshotReady = false
        this.snapshotInfo = null
        this.componentFilters = {}
        await this.loadSnapshot()
      } catch {
        this.$toast.error(this.$t('Erro ao atualizar dados'))
      } finally {
        this.updatingData = false
        this.unlocked = false
      }
    },

    async exportPdf() {
      this.pdfLoading = true
      try {
        const response = await this.apiService.get(
          `syntheticdashboardsnapshot/pdf/${this.institutionId}`
        )
        const data = `data:application/octet-stream;base64,${response.content}`
        const link = document.createElement('a')
        link.href = data
        link.setAttribute('download', 'dashboard-sintetico.pdf')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch {
        this.$toast.error(this.$t('Erro ao gerar PDF'))
      } finally {
        this.pdfLoading = false
      }
    },

    reset() {
      this.unidadeIds = []
      this.filter.page = 1
      this.filter.pageSize = 7
      this.filter.institutionId = this.institutionId
      this.filter.unidadeIds = []
      this.filter.standardEquipment = []
      this.filter.defectCauses = []
      this.filter.departmentIds = []
      this.startDate = moment()
        .add(-1, 'month')
        .clone()
        .startOf('month')
        .format('YYYY-MM-DD')
      this.endDate = moment()
        .add(-1, 'month')
        .clone()
        .endOf('month')
        .format('YYYY-MM-DD')
      this.setDates()
    },

    removeUnity(index) {
      this.unidadeIds.splice(index, 1)
      this.unidadeIds = [...this.unidadeIds]
    },

    removeStandardEquipment(index) {
      this.filter.standardEquipment.splice(index, 1)
      this.filter.standardEquipment = [...this.filter.standardEquipment]
    },

    removeCauseDefect(index) {
      this.filter.defectCauses.splice(index, 1)
      this.filter.defectCauses = [...this.filter.defectCauses]
    },

    removeDepartment(index) {
      this.filter.departmentIds.splice(index, 1)
      this.filter.departmentIds = [...this.filter.departmentIds]
    },

    async getInstitutions() {
      this.institutionLoading = true
      try {
        const response = await this.apiService.get('institution/list')
        this.institutions = response.content
      } finally {
        this.institutionLoading = false
      }
    },

    async getUnidades() {
      this.unidadeLoading = true
      try {
        const response = await this.apiService.get(
          `unidade/list/${this.institutionId}`
        )
        this.unidades = response.content
      } finally {
        this.unidadeLoading = false
      }
    },

    async getUserUnidades() {
      this.unidadeLoading = true
      try {
        const response = await this.apiService.get(
          `unidade/listByUserAndInstitution/${this.institutionId}`
        )
        this.unidades = response.content
      } finally {
        this.unidadeLoading = false
      }
    },

    async getDepartments() {
      this.departmentLoading = true
      try {
        const response = await this.apiService.get(
          `department/list/${this.institutionId}`
        )
        this.localDepartments = response.content
      } finally {
        this.departmentLoading = false
      }
    },

    async getStandardEquipments() {
      try {
        this.standardEquipments =
          await this.generalService.getStandardEquipmentsByInstitution(
            this.institutionId
          )
      } catch {
        this.standardEquipments = []
      }
    },
  },
}
</script>

<style>
.v-tabs--icons-and-text > .v-tabs-bar .v-tab {
  flex-direction: row-reverse;
}

.v-tab.v-tab {
  color: #bbcf20;
}

.v-tab {
  text-transform: none;
}

.v-tab--active {
  background-color: #e7eaeb;
}

.synthetic-dashboard .indicator-card__header .secondary-button {
  display: none !important;
}
</style>
